import React, { useState } from 'react';
import { ArrowRight, GraduationCap, Globe, Users, CheckCircle, Star, Award, BookOpen, Heart, Stethoscope, UserCheck, FileText, Clock, MapPin } from 'lucide-react';
import { Button } from '../components/ui/button';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import WhatsAppButton from '../components/WhatsAppButton';
import ApplicationForm from '../components/ApplicationForm';

const MBBSAbroadPage = () => {
  const [applyFormOpen, setApplyFormOpen] = useState(false);

  const countries = [
    {
      name: "Russia",
      universities: 45,
      avgFees: "$3,000 - $6,000",
      duration: "6 Years",
      language: "English/Russian",
      recognition: "WHO, MCI Approved"
    },
    {
      name: "Ukraine",
      universities: 32,
      avgFees: "$3,500 - $5,500",
      duration: "6 Years",
      language: "English",
      recognition: "WHO, MCI Approved"
    },
    {
      name: "Georgia",
      universities: 18,
      avgFees: "$4,000 - $7,000",
      duration: "6 Years",
      language: "English",
      recognition: "WHO, MCI Approved"
    },
    {
      name: "Kazakhstan",
      universities: 25,
      avgFees: "$3,200 - $5,800",
      duration: "6 Years",
      language: "English/Russian",
      recognition: "WHO, MCI Approved"
    },
    {
      name: "Philippines",
      universities: 28,
      avgFees: "$2,500 - $4,500",
      duration: "5.5 Years",
      language: "English",
      recognition: "WHO, MCI Approved"
    },
    {
      name: "China",
      universities: 42,
      avgFees: "$3,800 - $8,000",
      duration: "6 Years",
      language: "English/Chinese",
      recognition: "WHO, MCI Approved"
    }
  ];

  const benefits = [
    {
      icon: Globe,
      title: "Global Recognition",
      description: "Degrees from WHO and MCI approved universities with worldwide recognition"
    },
    {
      icon: Users,
      title: "Affordable Education",
      description: "Quality medical education at fraction of the cost compared to private colleges"
    },
    {
      icon: BookOpen,
      title: "English Medium",
      description: "Most universities offer MBBS programs in English medium instruction"
    },
    {
      icon: Award,
      title: "No Donation",
      description: "Direct admission without any capitation fees or donations required"
    },
    {
      icon: Heart,
      title: "Clinical Exposure",
      description: "Extensive hands-on clinical training in well-equipped hospitals"
    },
    {
      icon: UserCheck,
      title: "Easy Admission",
      description: "Simple admission process with minimal entrance exam requirements"
    }
  ];

  const process = [
    {
      step: "01",
      title: "Counseling & Country Selection",
      description: "Expert guidance to choose the right country and university based on your preferences and budget"
    },
    {
      step: "02",
      title: "Documentation & Application",
      description: "Complete assistance with document preparation and university application submission"
    },
    {
      step: "03",
      title: "Admission & Invitation Letter",
      description: "Secure admission confirmation and invitation letter from the selected university"
    },
    {
      step: "04",
      title: "Visa Processing",
      description: "Complete visa assistance including document preparation and embassy guidance"
    },
    {
      step: "05",
      title: "Pre-Departure Support",
      description: "Travel arrangements, accommodation booking, and pre-departure orientation"
    },
    {
      step: "06",
      title: "Post-Arrival Assistance",
      description: "Airport pickup, university registration, and settling-in support"
    }
  ];

  const stats = [
    { value: "2000+", label: "Students Placed" },
    { value: "50+", label: "Partner Universities" },
    { value: "15+", label: "Countries" },
    { value: "98%", label: "Visa Success Rate" }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      {/* Hero Section */}
      <section className="pt-24 pb-20 xl:pb-32 bg-gradient-to-br from-navy-50 via-white to-brand-50 relative overflow-hidden">
        <div className="max-w-7xl xl:max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 xl:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 xl:gap-16 items-center">
            
            {/* Left Content */}
            <div>
              <div className="inline-block px-6 py-2 bg-brand-100 text-brand-600 rounded-full text-sm xl:text-base font-medium mb-6 xl:mb-8">
                Medical Education Abroad
              </div>
              
              <h1 className="text-5xl md:text-6xl xl:text-7xl font-bold text-gray-900 mb-6 xl:mb-8 leading-tight">
                MBBS
                <span className="text-brand-600 block">Abroad</span>
              </h1>
              
              <p className="text-xl xl:text-2xl text-gray-600 mb-8 xl:mb-12 leading-relaxed">
                At Xpert Advisor, we guide students who wish to pursue MBBS abroad by helping them choose recognized medical universities that offer quality education and affordable tuition fees.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-6 xl:gap-8 mb-12 xl:mb-16">
                <Button 
                  onClick={() => setApplyFormOpen(true)}
                  className="px-8 xl:px-12 py-4 xl:py-6 bg-navy-900 hover:bg-navy-800 text-white text-lg xl:text-xl font-semibold rounded-lg shadow-lg"
                >
                  Start Your Journey
                  <ArrowRight className="w-5 h-5 xl:w-6 xl:h-6 ml-2" />
                </Button>
                
                <Button 
                  variant="outline"
                  className="px-8 xl:px-12 py-4 xl:py-6 border-2 border-navy-900 text-brand-600 hover:bg-brand-50 text-lg xl:text-xl font-semibold rounded-lg"
                >
                  Download Brochure
                </Button>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6 xl:gap-8">
                {stats.map((stat, index) => (
                  <div key={index} className="text-center">
                    <div className="text-2xl xl:text-3xl font-bold text-brand-600 mb-1">{stat.value}</div>
                    <div className="text-sm xl:text-base text-gray-600">{stat.label}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Right Image */}
            <div className="relative">
              <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                <img 
                  src="https://images.unsplash.com/photo-1559839734-2b71ea197ec2?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80"
                  alt="Medical Students"
                  className="w-full h-[500px] xl:h-[600px] object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-navy-950/40 to-transparent"></div>
              </div>
              
              {/* Floating Card */}
              <div className="absolute -bottom-6 -left-6 bg-white rounded-xl p-6 shadow-xl border border-gray-100">
                <div className="flex items-center space-x-4">
                  <div className="w-12 h-12 bg-brand-100 rounded-full flex items-center justify-center">
                    <Stethoscope className="w-6 h-6 text-brand-600" />
                  </div>
                  <div>
                    <div className="text-lg font-bold text-gray-900">WHO Approved</div>
                    <div className="text-sm text-gray-600">Global Recognition</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section className="py-20 xl:py-32 bg-white">
        <div className="max-w-7xl xl:max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 xl:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 xl:gap-16 items-center">
            
            {/* Left Image */}
            <div className="relative">
              <img 
                src="https://images.unsplash.com/photo-1582750433449-648ed127bb54?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80"
                alt="Medical Professional"
                className="w-full h-[400px] xl:h-[500px] object-cover rounded-2xl shadow-lg"
              />
            </div>

            {/* Right Content */}
            <div>
              <h2 className="text-4xl xl:text-5xl font-bold text-gray-900 mb-6 xl:mb-8">
                Your Path to Medical Excellence
              </h2>
              
              <p className="text-lg xl:text-xl text-gray-600 mb-6 xl:mb-8 leading-relaxed">
                Our team supports students throughout the admission process, from selecting the right country and university to completing applications and securing admissions.
              </p>
              
              <p className="text-lg xl:text-xl text-gray-600 mb-8 xl:mb-10 leading-relaxed">
                We also provide guidance on eligibility requirements, documentation, and entrance exams where applicable. Our goal is to help students make informed decisions and begin their medical career with confidence.
              </p>

              <div className="space-y-4 xl:space-y-6">
                <div className="flex items-center">
                  <CheckCircle className="w-6 h-6 xl:w-7 xl:h-7 text-brand-500 mr-4" />
                  <span className="text-lg xl:text-xl text-gray-700">Complete admission guidance</span>
                </div>
                <div className="flex items-center">
                  <CheckCircle className="w-6 h-6 xl:w-7 xl:h-7 text-brand-500 mr-4" />
                  <span className="text-lg xl:text-xl text-gray-700">Visa processing support</span>
                </div>
                <div className="flex items-center">
                  <CheckCircle className="w-6 h-6 xl:w-7 xl:h-7 text-brand-500 mr-4" />
                  <span className="text-lg xl:text-xl text-gray-700">Pre & post-departure assistance</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 xl:py-32 bg-gray-50">
        <div className="max-w-7xl xl:max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 xl:px-12">
          <div className="text-center mb-16 xl:mb-20">
            <h2 className="text-4xl xl:text-5xl font-bold text-gray-900 mb-6 xl:mb-8">
              Why Choose MBBS Abroad?
            </h2>
            <p className="text-xl xl:text-2xl text-gray-600 max-w-3xl xl:max-w-4xl mx-auto">
              Discover the advantages of pursuing your medical degree internationally
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 xl:gap-12">
            {benefits.map((benefit, index) => (
              <div key={index} className="bg-white rounded-xl p-8 xl:p-10 shadow-sm border border-gray-200 hover:shadow-md transition-shadow">
                <div className="w-16 h-16 xl:w-20 xl:h-20 bg-brand-100 rounded-xl flex items-center justify-center mb-6 xl:mb-8">
                  <benefit.icon className="w-8 h-8 xl:w-10 xl:h-10 text-brand-600" />
                </div>
                <h3 className="text-xl xl:text-2xl font-bold text-gray-900 mb-4 xl:mb-6">{benefit.title}</h3>
                <p className="text-gray-600 xl:text-lg leading-relaxed">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Countries Section */}
      <section className="py-20 xl:py-32 bg-white">
        <div className="max-w-7xl xl:max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 xl:px-12">
          <div className="text-center mb-16 xl:mb-20">
            <h2 className="text-4xl xl:text-5xl font-bold text-gray-900 mb-6 xl:mb-8">
              Popular Destinations for MBBS
            </h2>
            <p className="text-xl xl:text-2xl text-gray-600 max-w-3xl xl:max-w-4xl mx-auto">
              Explore top countries offering quality medical education at affordable costs
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 xl:gap-12">
            {countries.map((country, index) => (
              <div key={index} className="bg-gradient-to-br from-brand-50 to-navy-50 rounded-xl p-8 xl:p-10 border border-gray-200 hover:shadow-lg transition-all duration-300">
                <div className="flex items-center justify-between mb-6 xl:mb-8">
                  <h3 className="text-2xl xl:text-3xl font-bold text-gray-900">{country.name}</h3>
                  <MapPin className="w-6 h-6 xl:w-8 xl:h-8 text-brand-600" />
                </div>
                
                <div className="space-y-4 xl:space-y-6">
                  <div className="flex justify-between">
                    <span className="text-gray-600 xl:text-lg">Universities:</span>
                    <span className="font-semibold text-gray-900 xl:text-lg">{country.universities}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600 xl:text-lg">Avg. Fees:</span>
                    <span className="font-semibold text-gray-900 xl:text-lg">{country.avgFees}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600 xl:text-lg">Duration:</span>
                    <span className="font-semibold text-gray-900 xl:text-lg">{country.duration}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600 xl:text-lg">Language:</span>
                    <span className="font-semibold text-gray-900 xl:text-lg">{country.language}</span>
                  </div>
                  <div className="pt-4 xl:pt-6 border-t border-gray-200">
                    <div className="flex items-center">
                      <CheckCircle className="w-5 h-5 xl:w-6 xl:h-6 text-brand-500 mr-2" />
                      <span className="text-sm xl:text-base text-gray-600">{country.recognition}</span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-20 xl:py-32 bg-gray-50">
        <div className="max-w-7xl xl:max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 xl:px-12">
          <div className="text-center mb-16 xl:mb-20">
            <h2 className="text-4xl xl:text-5xl font-bold text-gray-900 mb-6 xl:mb-8">
              Our Admission Process
            </h2>
            <p className="text-xl xl:text-2xl text-gray-600 max-w-3xl xl:max-w-4xl mx-auto">
              Step-by-step guidance from application to arrival
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 xl:gap-12">
            {process.map((step, index) => (
              <div key={index} className="relative">
                <div className="bg-white rounded-xl p-8 xl:p-10 shadow-sm border border-gray-200 hover:shadow-md transition-shadow">
                  <div className="w-16 h-16 xl:w-20 xl:h-20 bg-navy-900 rounded-full flex items-center justify-center mb-6 xl:mb-8">
                    <span className="text-2xl xl:text-3xl font-bold text-white">{step.step}</span>
                  </div>
                  <h3 className="text-xl xl:text-2xl font-bold text-gray-900 mb-4 xl:mb-6">{step.title}</h3>
                  <p className="text-gray-600 xl:text-lg leading-relaxed">{step.description}</p>
                </div>
                
                {/* Connector Line */}
                {index < process.length - 1 && (
                  <div className="hidden lg:block absolute top-1/2 -right-6 xl:-right-8 w-12 xl:w-16 h-0.5 bg-brand-200"></div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 xl:py-32 bg-navy-900 relative overflow-hidden">
        <div className="absolute inset-0">
          <img 
            src="https://images.unsplash.com/photo-1576091160399-112ba8d25d1f?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80"
            alt="Medical Team"
            className="w-full h-full object-cover opacity-20"
          />
        </div>
        
        <div className="relative z-10 max-w-4xl xl:max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 xl:px-12 text-center">
          <h2 className="text-4xl xl:text-5xl font-bold text-white mb-6 xl:mb-8">
            Ready to Start Your Medical Journey?
          </h2>
          <p className="text-xl xl:text-2xl text-navy-100 mb-12 xl:mb-16 max-w-3xl xl:max-w-4xl mx-auto">
            Join thousands of students who have successfully pursued MBBS abroad with our expert guidance and support.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-6 xl:gap-8 justify-center">
            <Button 
              onClick={() => setApplyFormOpen(true)}
              className="px-12 xl:px-16 py-4 xl:py-6 bg-white text-brand-600 hover:bg-gray-100 text-lg xl:text-xl font-semibold rounded-lg shadow-lg"
            >
              Apply Now
              <ArrowRight className="w-5 h-5 xl:w-6 xl:h-6 ml-2" />
            </Button>
            
            <Button 
              variant="outline" 
              className="px-12 xl:px-16 py-4 xl:py-6 border-2 border-white text-white hover:bg-white hover:text-brand-600 text-lg xl:text-xl font-semibold rounded-lg"
            >
              Get Free Counseling
            </Button>
          </div>

          <div className="mt-12 xl:mt-16 flex flex-wrap justify-center items-center gap-8 xl:gap-12 text-navy-100">
            <div className="flex items-center">
              <Award className="w-5 h-5 xl:w-6 xl:h-6 mr-2" />
              <span className="xl:text-lg">WHO Approved Universities</span>
            </div>
            <div className="flex items-center">
              <Globe className="w-5 h-5 xl:w-6 xl:h-6 mr-2" />
              <span className="xl:text-lg">Global Recognition</span>
            </div>
            <div className="flex items-center">
              <Users className="w-5 h-5 xl:w-6 xl:h-6 mr-2" />
              <span className="xl:text-lg">Expert Guidance</span>
            </div>
          </div>
        </div>
      </section>

      <Footer />
      <WhatsAppButton />
      
      {/* Application Form Modal */}
      <ApplicationForm open={applyFormOpen} onOpenChange={setApplyFormOpen} />
    </div>
  );
};

export default MBBSAbroadPage;